import React from 'react'
import { withFirebase } from '../../components/Fb'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import moment from 'moment'
import KpiCard from '../../components/Report/WeeklySummary/KpiCard'
import SalesSparklineCard from '../../components/Report/WeeklySummary/SalesSparklineCard'
import CategorySalesCard from '../../components/Report/WeeklySummary/CategorySalesCard'
import ProductTypeSalesCard from '../../components/Report/WeeklySummary/ProductTypeSalesCard'
import OccupancyByTypeCard from '../../components/Report/WeeklySummary/OccupancyByTypeCard'
import DailyBreakdownTable from '../../components/Report/WeeklySummary/DailyBreakdownTable'
import CategoryBreakdownTable from '../../components/Report/WeeklySummary/CategoryBreakdownTable'

const isoWeekRange = (selectedDate) => {
  const m = moment.utc(selectedDate, 'YYYY-MM-DD')
  return {
    start: m.clone().startOf('isoWeek').format('YYYY-MM-DD'),
    end: m.clone().endOf('isoWeek').format('YYYY-MM-DD'),
  }
}

const previousIsoWeekRange = (selectedDate) => {
  const m = moment.utc(selectedDate, 'YYYY-MM-DD').subtract(7, 'day')
  return {
    start: m.clone().startOf('isoWeek').format('YYYY-MM-DD'),
    end: m.clone().endOf('isoWeek').format('YYYY-MM-DD'),
  }
}

// Returns ranges for the N most recent ISO weeks ending at selectedDate's week, oldest first.
const lastNWeeksRanges = (selectedDate, n) => {
  const ranges = []
  for (let i = n - 1; i >= 0; i--) {
    const m = moment.utc(selectedDate, 'YYYY-MM-DD').subtract(i * 7, 'day')
    ranges.push({
      start: m.clone().startOf('isoWeek').format('YYYY-MM-DD'),
      end: m.clone().endOf('isoWeek').format('YYYY-MM-DD'),
      label: `Vk ${m.isoWeek()}`,
    })
  }
  return ranges
}

const sumCost = (rows) => (rows || []).reduce((acc, r) => acc + (Number(r.cost) || 0), 0)

const WEEKDAY_LABELS = ['Ma', 'Ti', 'Ke', 'To', 'Pe']
const WEEKEND_LABELS = ['La', 'Su']

const rowWeekdayLabel = (row) => {
  if (row && row.day) return row.day
  if (row && row.dayIndex) {
    const idx = moment.utc(row.dayIndex, 'YYYY-MM-DD').isoWeekday() - 1
    return ['Ma', 'Ti', 'Ke', 'To', 'Pe', 'La', 'Su'][idx]
  }
  return null
}

// Aggregates sales rows by row.producttype. Returns [] when none of the rows
// carry a producttype field (backend hasn't shipped it yet) so the card can
// show its "vaatii backend-päivityksen" placeholder.
const aggregateByProductType = (rowsCurrent, rowsPrevious) => {
  const all = [...(rowsCurrent || []), ...(rowsPrevious || [])]
  const hasField = all.some((r) => r && typeof r.producttype === 'string' && r.producttype.length > 0)
  if (!hasField) return []
  const map = new Map()
  const accum = (rows, key) => {
    for (const r of rows || []) {
      const t = (r && r.producttype) || 'other'
      const cur = map.get(t) || { type: t, current: 0, previous: 0 }
      cur[key] += Number(r.cost) || 0
      map.set(t, cur)
    }
  }
  accum(rowsCurrent, 'current')
  accum(rowsPrevious, 'previous')
  return Array.from(map.values()).sort((a, b) => (b.current + b.previous) - (a.current + a.previous))
}

// Computes occupancy split by reservationtype (single = irto, recurring = vakio)
// for the given allowed weekday labels. Returns null per branch when no rows
// carry the reservationtype field — the card switches to its placeholder.
const occupancyByType = (rows, allowedDays) => {
  if (!rows || rows.length === 0) return { irto: null, vakio: null }
  const hasField = rows.some((r) => r && (r.reservationtype === 'single' || r.reservationtype === 'recurring'))
  if (!hasField) return { irto: null, vakio: null }
  const acc = { single: { occ: 0, win: 0 }, recurring: { occ: 0, win: 0 } }
  for (const r of rows) {
    const wd = rowWeekdayLabel(r)
    if (allowedDays && !allowedDays.includes(wd)) continue
    const bucket = r.reservationtype === 'recurring' ? acc.recurring : acc.single
    bucket.occ += Number(r.occupancy) || 0
    bucket.win += Number(r.windowLength) || 0
  }
  return {
    irto: acc.single.win > 0 ? (acc.single.occ / acc.single.win) * 100 : null,
    vakio: acc.recurring.win > 0 ? (acc.recurring.occ / acc.recurring.win) * 100 : null,
  }
}

// Builds 7 rows (Mon-Sun) from busyraw rows + sales rows for the same week.
// busyrawRows supply occupancy + windowLength; salesRows supply sales €.
const aggregateByDay = (busyrawRows, salesRows, weekStart) => {
  const map = new Map()
  const start = moment.utc(weekStart, 'YYYY-MM-DD').startOf('isoWeek')
  for (let i = 0; i < 7; i++) {
    const d = start.clone().add(i, 'day')
    const key = d.format('YYYY-MM-DD')
    map.set(key, {
      date: key,
      dateShort: d.format('D.M.'),
      dayLabel: ['Ma', 'Ti', 'Ke', 'To', 'Pe', 'La', 'Su'][i],
      sales: 0,
      occupancy: 0,
      windowLength: 0,
    })
  }
  for (const r of busyrawRows || []) {
    const row = map.get(r.dayIndex)
    if (!row) continue
    row.occupancy += Number(r.occupancy) || 0
    row.windowLength += Number(r.windowLength) || 0
  }
  for (const r of salesRows || []) {
    const dateKey = r.occurency
      ? moment.utc(r.occurency).format('YYYY-MM-DD')
      : null
    const row = dateKey ? map.get(dateKey) : null
    if (!row) continue
    row.sales += Number(r.cost) || 0
  }
  return Array.from(map.values())
}

const aggregateByCategory = (rowsCurrent, rowsPrevious) => {
  const map = new Map()
  const accum = (rows, key) => {
    for (const r of rows || []) {
      const cat = r.category || '—'
      const cur = map.get(cat) || { category: cat, current: 0, previous: 0 }
      cur[key] += Number(r.cost) || 0
      map.set(cat, cur)
    }
  }
  accum(rowsCurrent, 'current')
  accum(rowsPrevious, 'previous')
  return Array.from(map.values()).sort((a, b) => (b.current + b.previous) - (a.current + a.previous))
}

const occupancyPercent = (rows, allowedDays) => {
  if (!rows || rows.length === 0) return null
  let occ = 0
  let win = 0
  for (const r of rows) {
    const wd = rowWeekdayLabel(r)
    if (allowedDays && !allowedDays.includes(wd)) continue
    occ += Number(r.occupancy) || 0
    win += Number(r.windowLength) || 0
  }
  if (win === 0) return null
  return (occ / win) * 100
}

class WeeklySummaryContainer extends React.Component {
  state = {
    selectedDate: moment.utc().format('YYYY-MM-DD'),
    salesCurrent: null,
    salesPrevious: null,
    salesTrend: [],
    productTypeSales: [],
    currentWeekSalesRows: [],
    salesLoading: true,
    weekdayOccupancyCurrent: null,
    weekdayOccupancyPrevious: null,
    weekendOccupancyCurrent: null,
    weekendOccupancyPrevious: null,
    categorySales: [],
    weekdayByType: { irto: { current: null, previous: null }, vakio: { current: null, previous: null } },
    weekendByType: { irto: { current: null, previous: null }, vakio: { current: null, previous: null } },
    currentWeekBusyrawRows: [],
    occupancyLoading: true,
  }

  componentDidMount() {
    this.fetchAll()
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.selectedDate !== this.state.selectedDate) {
      this.fetchAll()
    }
  }

  moveDate = (days) => {
    const next = days === 0
      ? moment.utc().format('YYYY-MM-DD')
      : moment.utc(this.state.selectedDate).add(days, 'day').format('YYYY-MM-DD')
    this.setState({ selectedDate: next })
  }

  fetchAll = () => {
    this.fetchSales()
    this.fetchOccupancy()
  }

  fetchOccupancy = async () => {
    this.setState({ occupancyLoading: true })
    const cur = isoWeekRange(this.state.selectedDate)
    const prev = previousIsoWeekRange(this.state.selectedDate)
    try {
      const [curRes, prevRes] = await Promise.all([
        this.props.firebase.getReport(cur.start, cur.end, 'busyraw', '06:00', '23:00', true),
        this.props.firebase.getReport(prev.start, prev.end, 'busyraw', '06:00', '23:00', true),
      ])
      const curRows = (curRes && curRes.data && curRes.data.rows) || []
      const prevRows = (prevRes && prevRes.data && prevRes.data.rows) || []
      const wkCur = occupancyByType(curRows, WEEKDAY_LABELS)
      const wkPrev = occupancyByType(prevRows, WEEKDAY_LABELS)
      const weCur = occupancyByType(curRows, WEEKEND_LABELS)
      const wePrev = occupancyByType(prevRows, WEEKEND_LABELS)
      this.setState({
        weekdayOccupancyCurrent: occupancyPercent(curRows, WEEKDAY_LABELS),
        weekdayOccupancyPrevious: occupancyPercent(prevRows, WEEKDAY_LABELS),
        weekendOccupancyCurrent: occupancyPercent(curRows, WEEKEND_LABELS),
        weekendOccupancyPrevious: occupancyPercent(prevRows, WEEKEND_LABELS),
        categorySales: aggregateByCategory(curRows, prevRows),
        weekdayByType: {
          irto: { current: wkCur.irto, previous: wkPrev.irto },
          vakio: { current: wkCur.vakio, previous: wkPrev.vakio },
        },
        weekendByType: {
          irto: { current: weCur.irto, previous: wePrev.irto },
          vakio: { current: weCur.vakio, previous: wePrev.vakio },
        },
        currentWeekBusyrawRows: curRows,
        occupancyLoading: false,
      })
    } catch (e) {
      console.error('weekly summary occupancy fetch failed', e)
      this.setState({
        weekdayOccupancyCurrent: null,
        weekdayOccupancyPrevious: null,
        weekendOccupancyCurrent: null,
        weekendOccupancyPrevious: null,
        categorySales: [],
        weekdayByType: { irto: { current: null, previous: null }, vakio: { current: null, previous: null } },
        weekendByType: { irto: { current: null, previous: null }, vakio: { current: null, previous: null } },
        currentWeekBusyrawRows: [],
        occupancyLoading: false,
      })
    }
  }

  fetchSales = async () => {
    this.setState({ salesLoading: true })
    const ranges = lastNWeeksRanges(this.state.selectedDate, 4)
    try {
      const responses = await Promise.all(
        ranges.map((r) => this.props.firebase.getReport(r.start, r.end, 'sales', '00:00', '23:59'))
      )
      const rowsByWeek = responses.map((res) => (res && res.data && res.data.rows) || [])
      const totals = rowsByWeek.map((rows) => sumCost(rows))
      const salesTrend = ranges.map((r, i) => ({ label: r.label, total: totals[i] }))
      const curRows = rowsByWeek[rowsByWeek.length - 1]
      const prevRows = rowsByWeek[rowsByWeek.length - 2]
      this.setState({
        salesTrend,
        salesCurrent: totals[totals.length - 1],
        salesPrevious: totals[totals.length - 2],
        productTypeSales: aggregateByProductType(curRows, prevRows),
        currentWeekSalesRows: curRows,
        salesLoading: false,
      })
    } catch (e) {
      console.error('weekly summary sales fetch failed', e)
      this.setState({ salesTrend: [], salesCurrent: null, salesPrevious: null, productTypeSales: [], currentWeekSalesRows: [], salesLoading: false })
    }
  }

  renderWeekNavigation = () => {
    const m = moment.utc(this.state.selectedDate, 'YYYY-MM-DD')
    const start = m.clone().startOf('isoWeek')
    const end = m.clone().endOf('isoWeek')
    const weekNumber = m.isoWeek()
    const sameMonth = start.month() === end.month()
    const range = sameMonth
      ? `${start.format('D.')} – ${end.format('D.M.YYYY')}`
      : `${start.format('D.M.')} – ${end.format('D.M.YYYY')}`
    const arrowSx = { cursor: 'pointer', mx: 0.5 }
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
        <KeyboardArrowLeftIcon onClick={() => this.moveDate(-7)} sx={arrowSx} titleAccess='Edellinen viikko' />
        <Box component='strong'>Vk {weekNumber} ({range})</Box>
        <KeyboardArrowRightIcon onClick={() => this.moveDate(7)} sx={arrowSx} titleAccess='Seuraava viikko' />
        <Button size='small' variant='outlined' sx={{ ml: 1 }} onClick={() => this.moveDate(0)}>
          Tämä viikko
        </Button>
      </Box>
    )
  }

  render() {
    const {
      selectedDate,
      salesCurrent, salesPrevious, salesTrend, productTypeSales, currentWeekSalesRows, salesLoading,
      weekdayOccupancyCurrent, weekdayOccupancyPrevious,
      weekendOccupancyCurrent, weekendOccupancyPrevious,
      categorySales, weekdayByType, weekendByType,
      currentWeekBusyrawRows,
      occupancyLoading,
    } = this.state
    const dailyBreakdown = aggregateByDay(currentWeekBusyrawRows, currentWeekSalesRows, selectedDate)
    return (
      <Box className='content'>
        <Box>
          <Typography variant='h5' sx={{ mb: 2 }}>Viikkokooste</Typography>
        </Box>
        <Box sx={{ mb: 2 }}>{this.renderWeekNavigation()}</Box>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <KpiCard
              title='Viikon myynti'
              value={salesCurrent}
              previous={salesPrevious}
              unit='€'
              loading={salesLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <KpiCard
              title='Arki varausaste'
              value={weekdayOccupancyCurrent}
              previous={weekdayOccupancyPrevious}
              unit='%'
              loading={occupancyLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <KpiCard
              title='Viikonlopun varausaste'
              value={weekendOccupancyCurrent}
              previous={weekendOccupancyPrevious}
              unit='%'
              loading={occupancyLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 3 }}>
            <SalesSparklineCard points={salesTrend} loading={salesLoading} />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <CategorySalesCard data={categorySales} loading={occupancyLoading} />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <ProductTypeSalesCard data={productTypeSales} loading={salesLoading} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <OccupancyByTypeCard
              title='Arki varausaste irto / vakio'
              data={weekdayByType}
              loading={occupancyLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <OccupancyByTypeCard
              title='Viikonlopun varausaste irto / vakio'
              data={weekendByType}
              loading={occupancyLoading}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <DailyBreakdownTable rows={dailyBreakdown} loading={salesLoading || occupancyLoading} />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <CategoryBreakdownTable data={categorySales} loading={occupancyLoading} />
          </Grid>
        </Grid>
      </Box>
    )
  }
}

export default withFirebase(WeeklySummaryContainer)
